import './AppRoadMap.scss';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { RoadmapRelease, RoadmapStatus } from './roadmap';
import NewsletterBanner from '../../../src/pages/Discover/NewsletterBanner/NewsletterBanner';
import {
  normalize,
  statusKey,
  findStatusMeta,
  deriveQuarterStatus,
  removePTags,
  getDrupalImageUrl
} from './roadmapFallbackData';
import { Divider } from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import { ReactComponent as CheckIcon} from '../../assets/images/roadMap/checkIcon.svg';
import {ReactComponent as InprogressIcon} from '../../assets/images/roadMap/inProgressIcon.svg';
import {ReactComponent as PlannedIcon} from '../../assets/images/roadMap/planned.svg';

const QUARTERS = ['Q1', 'Q2', 'Q3', 'Q4'];

// Remove any <a> wrapper from a title so the roadmap overview shows plain
// text (the linked title is only clickable on the quarter details page).
const stripAnchorTags = (str) =>
  (str || '').replace(/<a\b[^>]*>/gi, '').replace(/<\/a>/gi, '');

const AppRoadMap = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const releases = t('roadmap_new_releases', {
    returnObjects: true,
    defaultValue: []
  }) as RoadmapRelease[];

  const statuses = t('roadmap_new_statuses', {
    returnObjects: true,
    defaultValue: []
  }) as RoadmapStatus[];

  const heading = t('roadmap_new_heading', { defaultValue: '' });
  const description = t('roadmap_new_description', { defaultValue: '' });

  const readMoreLabel = t('newRoadmap.roadmap_read_more', { defaultValue: 'Read more' });

  // Locale-aware quarter labels (Q1..Q4 in English, T1..T4 in French).
  const newRoadmap = t('newRoadmap', {
    returnObjects: true,
    defaultValue: {}
  }) as { quarter?: string[] };
  const quarters =
    Array.isArray(newRoadmap?.quarter) && newRoadmap.quarter.length
      ? newRoadmap.quarter
      : QUARTERS;

  // Roadmap content comes from the Drupal CMS via i18n.
 const releaseList = useMemo(
    () => (Array.isArray(releases) ? releases : []).map((r) => {
      const raw: any = r;
      // Drupal sends "quarter " (with trailing space); find the value regardless.
      const quarter = Object.keys(raw).reduce(
        (val, k) => (k.trim().toLowerCase() === 'quarter' ? String(raw[k]).trim() : val),
        ''
      );
      const rawFlow = raw.bookingFlow;
      const bookingFlow: string[] = Array.isArray(rawFlow)
        ? rawFlow.map((f: any) => (typeof f === 'string' ? f : f.value))
        : rawFlow ? [rawFlow as string] : [];
      const rawServices = raw.webServices;
      const webServices: string[] = Array.isArray(rawServices)
        ? rawServices.map((s: any) => (typeof s === 'string' ? s : s.value))
        : rawServices ? [rawServices as string] : [];
      const rawSchema = raw.apiSchema ?? raw.api_schema;
      const api_schema: string = Array.isArray(rawSchema)
        ? rawSchema.map((s: any) => (typeof s === 'string' ? s : s.value)).join(', ')
        : rawSchema && typeof rawSchema === 'object'
        ? String(rawSchema.value || '')
        : rawSchema ? String(rawSchema) : '';
      return { ...raw, quarter, bookingFlow, webServices, api_schema };
    }),
    [releases]
  );
  const statusList = Array.isArray(statuses) ? statuses : [];
  
  // Show the 4 most recent years present in the data; otherwise fall back to
  // the current year + previous 3.
  const years = useMemo(() => {
    const dataYears = Array.from(
      new Set(releaseList.map((r) => Number(r.year)).filter(Boolean))
    ).sort((a, b) => b - a);
    if (dataYears.length) {
      return dataYears.slice(0, 4);
    }
    const current = new Date().getFullYear();
    return [current, current - 1, current - 2, current - 3];
  }, [releaseList]);

  const [selectedYear, setSelectedYear] = useState<number>(years[0]);

  const removeHtmlTags = (html: string) => html?.replace(/<[^>]*>/g, '') || '';

  // Fallback MUI icon when the CMS image is missing. Uses the locale-aware
  // status key so French statuses (Réalisé / En cours / Planifié) also map
  // to the correct icon.
  const fallbackIcon = (status?: string) => {
    const key = statusKey(status);
    if (key === 'achieved') {
      return <CheckIcon aria-hidden='true' className="statusIcon statusIcon--achieved" />;
    }
    if (key === 'progress') {
      return <InprogressIcon aria-hidden='true' className="statusIcon statusIcon--progress" />;
    }
    return <PlannedIcon aria-hidden='true' className="statusIcon statusIcon--planned" />;
  };

  const renderStatusIcon = (status?: string) => {
    const meta = findStatusMeta(statusList, status);
    const rawUrl = meta?.image
      ? typeof meta.image === 'string'
        ? meta.image
        : meta.image.url
      : '';
    const imageUrl = getDrupalImageUrl(rawUrl);
    if (imageUrl) {
      return (
        <img
          className="statusIcon"
          src={imageUrl}
          alt={typeof meta.image === 'object' ? meta.image.alt || '' : ''}
          aria-hidden="true"
        />
      );
    }
    return fallbackIcon(status);
  };

  const releasesForYear = releaseList.filter(
    (r) => Number(r.year) === Number(selectedYear)
  );

  return (
    <main className="roadmap">
      <div className="roadmap__intro">
        <div className="roadmap__intro-inner">
          {heading ? (
            <h1
              className="roadmap__heading">
                {removeHtmlTags(heading)}
              </h1>          ) : null}
          {description ? (
            <div
              className="roadmap__description"
              dangerouslySetInnerHTML={{ __html: description }}
            />
          ) : null}
        </div>
      </div>
      <div
        className="roadmap__tabs"
        role="tablist"
        aria-label="Roadmap year"
      >
        <div className="roadmap__tabs-inner">
          {years.map((year) => (
            <button
              type="button"
              role="tab"
              key={`year-${year}`}
              aria-selected={year === selectedYear}
              className={`roadmap__tab ${
                year === selectedYear ? 'roadmap__tab--active' : ''
              }`}
              onClick={() => setSelectedYear(year)}
            >
              {year}
            </button>
          ))}
        </div>
      </div>

      <div className="container">
      {statusList.length > 0 && (
        <ul className="roadmap__legend" aria-label="Status legend">
          {statusList.map((s) => (
            <li className="roadmap__legend-item" key={`legend-${s.id}-${s.status}`}>
              <span style={visuallyHidden}>&bull;&nbsp;</span>
              {renderStatusIcon(s.status)}
              <span>{s.status}</span>
            </li>
          ))}
        </ul>
      )}

      <div className="roadmap__cards">
        {quarters.map((quarter) => {
          const items = releasesForYear.filter(
            (r) => normalize(r.quarter) === normalize(quarter)
          );
          const quarterStatus = deriveQuarterStatus(items);

          return (
            <section
              className="roadmap__card"
              key={`${selectedYear}-${quarter}`}
              aria-label={`${quarter} ${selectedYear}`}
            >
              <div className="roadmap__card-header">
                <h2 className="roadmap__card-title">{quarter}</h2>
                {items.length > 0 && (
                  <span className='roadmap__card-status'>
                    {renderStatusIcon(quarterStatus)}
                    <span style={visuallyHidden}>{quarterStatus}</span>
                  </span>
                )}
              </div>

              {items.length > 0 ? (
                <ul className="roadmap__card-list">
                  {items.slice(0, 5).map((item) => (
                    <li className="roadmap__card-item" key={`item-${item.id}`}>
                      <span style={visuallyHidden}>&bull;&nbsp;</span>
                      {renderStatusIcon(item.status)}
                      <div className="roadmap__card-item-body">
                        <span
                          className="roadmap__card-item-text"
                          dangerouslySetInnerHTML={{
                            __html: stripAnchorTags(removePTags(item.title))
                          }}
                        />
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="roadmap__card-empty">
                  {t('newRoadmap.roadmap_updates_coming_soon', {
                    defaultValue: 'Updates coming soon'
                  })}
                </div>
              )}

              <Divider sx={{mt: '10px'}}/>

              <button
                type="button"
                className="roadmap__read-more"
                onClick={() =>
                  navigate(
                    `/${i18n.language}/api/roadmap/${selectedYear}/${quarter.toLowerCase()}`
                  )
                }
                disabled={items.length === 0}
                aria-label={`${readMoreLabel} ${quarter} ${selectedYear}`}
              >
                {readMoreLabel}
                <span aria-hidden="true"> &rsaquo;</span>
              </button>
            </section>
          );
        })}
      </div>
      <div>
        <NewsletterBanner/>
      </div>
      </div>
    </main>
  );
};

export default AppRoadMap;
